/**
 * GraphQL mutation boundary for CRM entities (Hub → Supabase GraphQL).
 *
 * All entity create/update paths that use `graphql-hooks` should run payloads through
 * `prepareEntityGraphQlMutationInput` so `processEntityDataForApi` consistently:
 * - strips invalid/empty JSON strings (jsonb columns reject "")
 * - serializes JSON objects and DECIMAL fields per Supabase GraphQL expectations
 * - resolves multi-user "me" when `currentUserId` is passed
 * - removes FTS / table UI-only fields (`GRAPHQL_FTS_UI_STRIP_KEYS`) on every entity
 *
 * Do not duplicate this logic in page handlers, inline editors, or modals — call mutations
 * from hooks only, or add processing inside the hook implementation.
 *
 * @see task-data-processing.ts — processEntityDataForApi
 * @see docs/testing/PLAYWRIGHT_E2E_SETUP.md — E2E smoke for critical flows
 */

import type { BaseEntity, SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { processEntityDataForApi } from "../task-data-processing";

/** Full-text search / table UI-only fields — stripped for every entity; never send to GraphQL */
export const GRAPHQL_FTS_UI_STRIP_KEYS = [
  "_isLoading",
  "_relevance",
  "_isPartial",
] as const;

export type PrepareEntityGraphQlMutationInputOptions = {
  mode: "insert" | "update";
  /** Extra keys to remove after `GRAPHQL_FTS_UI_STRIP_KEYS` (entity-specific only) */
  stripKeys?: readonly string[];
  currentUserId?: string;
};

/**
 * Clone input, strip Apollo/FTS metadata, then apply entity field processing for GraphQL.
 */
export function prepareEntityGraphQlMutationInput(
  input: Record<string, unknown>,
  fields: Record<string, SimpleFieldConfig>,
  options: PrepareEntityGraphQlMutationInputOptions
): Record<string, unknown> {
  const clone: Record<string, unknown> = { ...input };
  delete clone.__typename;
  for (const k of GRAPHQL_FTS_UI_STRIP_KEYS) {
    delete clone[k];
  }
  for (const k of options.stripKeys ?? []) {
    delete clone[k];
  }
  if (options.mode === "insert") {
    delete clone.id;
  }
  return processEntityDataForApi(clone as BaseEntity, fields, options.currentUserId) as Record<
    string,
    unknown
  >;
}
